
















import { Component, Mixins, Prop, Inject } from "vue-property-decorator";
import { CreateElement, VNode } from "vue";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";

// Components
import TemplateContent from "@/components/app/global/templates/contentForm.vue";
import ActionContentsTable from "@/components/app/admin/automation/actionContentsTable.vue";
import UseCustomField from "@/components/app/global/templates/invoices/useCustomField.vue";
import AdvancedDropDownButton from "@/components/app/global/templates/invoices/advancedDropdownButton.vue";
import NonDefaultMessage from "@/components/app/global/templates/templateFields/nonDefaultMessage.vue";

// Enums/Data
import { TemplateCodes } from "@/data/enums/templates";
import { UpmindObjectTypes } from "@/data/enums/objects";

// Types
import { ITemplate, ITemplateCategory } from "@/models/templates";
import {
  ITemplateProvider,
  ITemplateProviderMethods
} from "@/models/providers/templates";
import { TranslateResult } from "vue-i18n";
import { ScopedSlotReturnValue } from "vue/types/vnode";

type IInvoiceTemplateForm = Partial<
  Pick<ITemplate, "body" | "code" | "enabled" | "type"> & {
    category_code: ITemplateCategory["code"];
  }
>;

@Component({
  components: {
    "template-content": TemplateContent,
    "action-contents-table": ActionContentsTable,
    "use-custom-field": UseCustomField,
    "advanced-drop-down-button": AdvancedDropDownButton
  }
})
export default class InvoiceTemplateEditForm extends Mixins(HasValidation) {
  // Inject

  @Inject({ from: "templateProvider" })
  public templateProvider!: () => ITemplateProvider;

  @Inject({ from: "templateProviderMethods" })
  public $templateMethods!: ITemplateProviderMethods;

  // Props

  @Prop({ default: true })
  public showSubmitButton!: boolean;

  // Data

  public form: IInvoiceTemplateForm = {};

  // Life-cycle methods

  public created() {
    this.revertToInitialState();
  }

  // Computed

  public get components() {
    return {
      render: (h: CreateElement) => {
        return h("div", {}, [
          this.renderTemplateContentContextualise(h),
          this.renderTranslations(h)
        ]);
      }
    };
  }

  public get isFormChanged(): boolean {
    return !_.isEqual(this.form, this.initForm());
  }

  public get $templateData() {
    return this.templateProvider();
  }

  public get template() {
    return this.$templateData.template;
  }

  public get selectedCategory() {
    return _.find(this.$templateData.categories, [
      "code",
      this.form.category_code
    ]);
  }

  public get availableCategoriesForContent() {
    /**
     * Always injecting brand category + selected category
     */
    return _.uniqBy(
      _.compact([
        this.$templateData.brandCategory,
        this.form.category_code ? this.selectedCategory : null
      ]),
      "id"
    );
  }

  public get isDisabled() {
    return (
      !this.$templateData.isNew ||
      this.$templateData.isReadOnly ||
      this.$templateData.isProcessing
    );
  }

  public get isCustomTemplate() {
    return !_.isNull(this.template?.original_template_id);
  }

  public get newValues() {
    const newValues: any = _.omitBy(this.form, (v, k) => {
      return this.initForm()[k] === v;
    });

    return newValues;
  }

  public get defaultTemplateBody() {
    return this.template?.default
      ? this.template?.body
      : this.template?.original_template?.body || "";
  }

  // Methods

  public revertToInitialState() {
    this.form = this.initForm();
  }

  public initForm(): IInvoiceTemplateForm {
    return {
      body: this.template?.body,
      category_code: UpmindObjectTypes.INVOICE,
      code: TemplateCodes.INVOICE_PDF,
      enabled: !!this.template?.enabled && this.$templateData.canEdit,
      type: this.$templateData.templateType
    };
  }

  public async validateBeforeSubmit() {
    try {
      this.$templateMethods.setIsProcessing(true);

      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      await this.$templateMethods.updateTemplate(
        this.isCustomTemplate ? this.newValues : this.form,
        this.isCustomTemplate ? null : this.template.id
      );

      await this.$templateMethods.reloadData();

      this.form = this.initForm();

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.successfully_saved")
      });
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.$templateMethods.setIsProcessing(false);
    }
  }

  // Render functions

  public renderContextualise(
    defaultSlot: ScopedSlotReturnValue,
    {
      label,
      description,
      extras
    }: {
      label: string | TranslateResult;
      description: ScopedSlotReturnValue;
      extras?: VNode | null;
    }
  ) {
    const h = this.$createElement;

    return h("u-contextualise", {
      attrs: { "min-width": "36rem" },
      props: { title: label },
      scopedSlots: {
        description: () => description,
        default: () => defaultSlot,
        extras: () => extras
      }
    });
  }

  public renderCustomToggle(h: CreateElement) {
    return h(UseCustomField, {
      class: "has-margin-top-100",
      props: {
        value: this.form.enabled,
        isDisabled: this.isDisabled
      },
      on: {
        input: event => {
          this.$set(this.form, "enabled", event);
        }
      }
    });
  }

  public renderTemplateContentContextualise(h: CreateElement) {
    const renderDescriptionChildren = [
      h(
        "span",
        this.$t("_sentence.templates.invoice_template_context") as string
      ),
      this.renderNonDefaultMessage(h)
    ];

    return this.renderContextualise(this.renderTemplateContent(h), {
      label: this.$t("_.invoice_template"),
      description: h("span", {}, this.$_.compact(renderDescriptionChildren)),
      extras: h("div", {}, [
        this.renderCustomToggle(h),
        this.renderAdvancedActions(h)
      ])
    });
  }

  public renderNonDefaultMessage(h: CreateElement) {
    if (!this.template?.original_template_id) return;

    return h(NonDefaultMessage, {
      class: "has-margin-top-100",
      on: {
        restoreDefault: () => this.$templateMethods.restoreToDefault(),
        viewDefault: () => {
          this.$templateMethods.openViewContentModal({
            templateId: this.template.original_template_id
          });
        }
      }
    });
  }

  public renderTemplateContent(h: CreateElement) {
    const { template } = this.$templateData;
    return h(TemplateContent, {
      key: this.form.enabled
        ? (template?.id as string)
        : (template?.original_template_id as string),
      props: {
        body:
          this.form.enabled || !!template?.default
            ? this.form.body
            : template?.original_template?.body,
        codeMirrorProps: {
          height: "500px"
        },
        codeMirrorOptions: {
          lineNumbers: true,
          styleActiveLine: true
        },
        categories: this.availableCategoriesForContent,
        category: this.selectedCategory,
        processing: this.$templateData.isProcessing,
        isDisabled: this.isDisabled || !this.form.enabled,
        showBodyArgsAndDesc: this.form.enabled,
        showSubject: false,
        subject: "",
        template
      },
      on: {
        "update:body": e => (this.form.body = e)
      }
    });
  }

  public renderTranslations(h: CreateElement) {
    if (
      (this.isCustomTemplate && !this.template.enabled) ||
      !this.form.enabled ||
      !this.isCustomTemplate
    )
      return;

    return this.renderContextualise(
      h(ActionContentsTable, {
        props: {
          id: "NTCT1",
          categories: this.availableCategoriesForContent,
          template: this.$templateData.template,
          showSubject: false
        },
        on: {
          "contents-updated": async () => {
            this.$templateMethods.reloadContents();
          }
        }
      }),
      {
        label: this.$t("_.translations").toString(),
        description: this.$t("_sentence.templates.translations_desc").toString()
      }
    );
  }

  public renderAdvancedActions(h: CreateElement) {
    if (!this.form.enabled) return;

    return h(
      AdvancedDropDownButton,
      {
        class: "has-margin-top-100",
        props: {
          label: this.$t("_.advanced")
        },
        scopedSlots: {
          "dropdown-items": () =>
            h(
              "u-dropdown-item",
              {
                on: {
                  click: () => {
                    this.form.body = this.defaultTemplateBody;
                  }
                }
              },
              [this.$t("_action.reset_to_system_default") as string]
            )
        }
      },
      []
    );
  }
}
